import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, ArrowLeft } from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-700">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
              <Shield className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900"><BrandTitle>Privacy Policy</BrandTitle></h1>
              <p className="text-slate-600 text-sm">Last updated: May 7, 2026</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <div className="space-y-8 text-slate-700">

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
                <p>
                  <strong className="text-emerald-700">CARTIEAI LLC</strong>, a Texas limited liability company ("CARTIEAI", "we," "our," or "us"), provides a cloud financial intelligence platform that helps engineering and finance teams understand, attribute and reduce their cloud spend. This Privacy Policy describes — in plain language — what information we collect, why we collect it, who we share it with and the rights you have over it. If anything here is unclear, please email us at <a href="mailto:hello@cartieai.com" className="text-emerald-700">hello@cartieai.com</a> and we'll happily explain.
                </p>
                <p className="mt-3">
                  This Policy applies to: (a) the authenticated CARTIEAI application, (b) the public marketing site at <code className="text-xs bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded">cartieai.com</code>, and (c) free public tools we publish — including the <Link to="/pr-cost-predictor" className="text-emerald-700">PR-Time Cost Prediction</Link> calculator, the <Link to="/trust" className="text-emerald-700">Trust page</Link> security checks and the cornerstone-blog lead-magnet downloads.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">2.1 Account information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name and email address</li>
                  <li>Company name and role (optional)</li>
                  <li>Password — stored only as a one-way bcrypt hash; we cannot read it back</li>
                  <li>Multi-factor authentication tokens, if you enable MFA</li>
                  <li>Billing information — collected and processed by <strong>Stripe</strong>; we never see or store full card numbers</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">2.2 Cloud cost data you connect</h3>
                <p>
                  When you connect a cloud account (AWS, Azure, GCP, Snowflake, Databricks, DigitalOcean, MongoDB Atlas) we ingest your billing and usage data through read-only API connections. We use the minimum permissions required to read costs and never modify your infrastructure.
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Resource-level usage and cost line items</li>
                  <li>Service configurations and tags (used for cost allocation)</li>
                  <li>Billing reports, invoices and committed-spend records</li>
                </ul>
                <p className="mt-3 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20 text-sm">
                  <strong className="text-emerald-700">Stateless cloud credentials:</strong> Cloud-provider keys you paste into a one-shot audit tool are held in memory only for the duration of the scan and immediately discarded — never written to our database, disk or logs. Persistent integrations (e.g. AWS Cost &amp; Usage Reports, Snowflake) are stored encrypted at rest with strict tenant isolation.
                </p>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">2.3 Stripe data (Customer P&amp;L feature)</h3>
                <p>
                  If you enable our <em>Cost-per-Customer P&amp;L</em> feature, you authorise CARTIEAI to read — via your Stripe restricted key — the following from your Stripe account:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Customer list (id, email, name, metadata)</li>
                  <li>Subscriptions (active and recently cancelled)</li>
                  <li>Invoices for the trailing 90 days</li>
                </ul>
                <p className="mt-2">
                  We <strong>never</strong> request write permissions and we <strong>never</strong> see card numbers. Your Stripe key is stored encrypted at rest and you can revoke it from your Stripe dashboard at any time. Each tenant's Stripe data lives in a physically separated Mongo collection scoped to their organisation id.
                </p>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">2.4 Data you submit to free public tools</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>PR-Time Cost Predictor:</strong> the Terraform plan JSON you paste is processed in memory to compute a cost delta and is not persisted. Plans should not contain credentials — Terraform plans are configuration only.</li>
                  <li><strong>Trust page checks:</strong> the live security probes run server-side against our own infrastructure and do not collect data about you.</li>
                  <li><strong>Lead-magnet downloads / Vendor Due-Diligence pack:</strong> if you provide an email to download a checklist or DD pack, we store your email, name, company, magnet slug, IP address, user-agent and referrer for sales follow-up. See Section 6 (Retention) and Section 7 (Your Rights).</li>
                  <li><strong>Design-partner sign-ups:</strong> the email and company you submit on the PR-Cost page are stored in the same lead-capture system above and used solely to schedule a co-build conversation.</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">2.5 Usage and device information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Pages visited, features used, click events on our marketing pages</li>
                  <li>IP address, browser, device type, time-zone (used for security and abuse-prevention)</li>
                  <li>Email-engagement signals — when our founder follow-up emails contain a tracking pixel, we record opens and approximate location to prioritise replies. You can request pixel-free emails at any time by replying with "no tracking please".</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Operate, maintain and secure our services</li>
                  <li>Generate cost insights, anomaly alerts and AI-assisted recommendations</li>
                  <li>Send transactional emails (login codes, invoices, security alerts) — these cannot be opted out of while your account is active</li>
                  <li>Send occasional founder-led product updates — you can opt out from any email's footer</li>
                  <li>Process payments and prevent fraud</li>
                  <li>Improve our pricing models using <em>aggregated, de-identified</em> resource-type signals (e.g. "X% of plans include a NAT gateway"). We never train AI models on your raw cost data, customer lists or Stripe payloads.</li>
                  <li>Respond to support requests and comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Subprocessors</h2>
                <p>
                  We rely on a small, vetted set of third-party vendors to operate the service (for payment processing, transactional email, hosting, optional Slack alerting and AI-assisted features). For procurement and InfoSec reviewers we publish the current list — including each vendor's role and the categories of data shared — at <Link to="/subprocessors" className="text-emerald-700">cartieai.com/subprocessors</Link>.
                </p>
                <p className="mt-3">
                  Access to that page requires a quick email confirmation so we can notify reviewers when the list changes. We will publish updates at least 14 days before adding a new subprocessor that handles personal data.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Data Security</h2>
                <p>We follow industry-standard security practices that map to SOC 2 controls:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>AES-256 encryption at rest, TLS 1.3 in transit</li>
                  <li>Bcrypt password hashing, JWT-based session tokens, optional TOTP &amp; email MFA</li>
                  <li>Strict per-tenant data isolation enforced at the database-collection level (verified by automated tests on every release)</li>
                  <li>Audit logging of every privileged action, retained for 12 months</li>
                  <li>Role-based access control with least-privilege defaults</li>
                  <li>Quarterly internal security audits and dependency scans</li>
                </ul>
                <p className="mt-3 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20 text-sm">
                  <strong className="text-amber-400">Honest framing on certifications:</strong> we operate <em>SOC 2-aligned</em> controls today. Formal SOC 2 Type II audit and ISO 27001 certification are scheduled to begin around customer #25, when we qualify for compliance-platform startup programmes (Drata / Vanta) and engage an external CPA auditor. Our <Link to="/trust" className="text-emerald-700">Trust page</Link> publishes the live status, the planned timeline and the source policies (derived from the open-source <em>strongdm/comply</em> bundle).
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Data Retention</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account data</strong> — kept while your account is active; deleted within 30 days of cancellation.</li>
                  <li><strong>Cloud cost data</strong> — kept for the lesser of (a) your subscription term + 90 days or (b) any retention period you configure in account settings.</li>
                  <li><strong>Stripe customer cache</strong> — refreshed on each sync; deleted on disconnect.</li>
                  <li><strong>Audit logs</strong> — 12 months, then anonymised.</li>
                  <li><strong>Lead-magnet subscribers / design-partner sign-ups</strong> — retained until you ask to be removed; we honour all requests within 30 days.</li>
                  <li><strong>Backups</strong> — encrypted snapshots are retained for 35 days then permanently destroyed.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Your Rights</h2>
                <p>Whether or not you live in the EU/UK or California, we extend the following rights to every user:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access</strong> — request a copy of all personal data we hold about you</li>
                  <li><strong>Rectification</strong> — correct anything that is inaccurate</li>
                  <li><strong>Erasure</strong> — delete your account and associated data ("right to be forgotten")</li>
                  <li><strong>Portability</strong> — export your data in machine-readable JSON or CSV</li>
                  <li><strong>Restriction / objection</strong> — opt out of marketing emails, AI processing, or analytics tracking</li>
                  <li><strong>Withdraw consent</strong> — disconnect any cloud provider or Stripe at any time</li>
                </ul>
                <p className="mt-3">
                  To exercise any of these rights, email <a href="mailto:hello@cartieai.com" className="text-emerald-700">hello@cartieai.com</a> from the address on file. We respond within 30 days. There is no charge.
                </p>
                <p className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-slate-700">
                  <strong className="text-slate-900">California residents (CCPA):</strong> we <strong className="text-slate-900">do not sell</strong> your personal information. We have not sold or shared personal information for the purposes defined under CCPA in the past 12 months.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">8. International Transfers</h2>
                <p>
                  Our primary data hosting is in the United States (MongoDB Atlas, US-East region). If you access CARTIEAI from outside the US, your data is transferred to and processed in the US under Standard Contractual Clauses. We're happy to sign a Data Processing Agreement for EU/UK customers — email <a href="mailto:hello@cartieai.com" className="text-emerald-700">hello@cartieai.com</a> with subject <em>"DPA request"</em>.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Cookies and Similar Technologies</h2>
                <p>We use a small number of cookies and similar technologies:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Essential</strong> — session cookies, CSRF tokens. Always on; required for the app to function.</li>
                  <li><strong>Analytics</strong> — first-party page-view counters and click-tracking on our marketing pages. You can decline these via the cookie banner.</li>
                  <li><strong>Email-open tracking pixel</strong> — used in some founder follow-up emails (Section 2.5). Reply with "no tracking please" to opt out.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Children's Privacy</h2>
                <p>
                  CARTIEAI is a B2B product not intended for children under 18. We do not knowingly collect personal information from children. If you believe a child has submitted information to us, contact us and we will delete it.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Changes to This Policy</h2>
                <p>
                  When we make material changes we will email account holders at least 14 days before the new version takes effect. The "Last updated" date at the top will always reflect the effective version.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Contact Us</h2>
                <p>For any privacy question, request, or concern:</p>
                <div className="mt-4 p-4 bg-slate-100 rounded-lg">
                  <p><strong className="text-slate-900">Privacy lead:</strong> Lakshmi (founder)</p>
                  <p className="mt-2"><strong className="text-slate-900">Email:</strong> <a href="mailto:hello@cartieai.com" className="text-emerald-700">hello@cartieai.com</a></p>
                  <p className="mt-2"><strong className="text-slate-900">Phone:</strong> <a href="tel:+15126217610" className="text-emerald-700">+1 (512) 621-7610</a></p>
                  <p className="mt-2"><strong className="text-slate-900">Legal entity:</strong> CARTIEAI LLC</p>
                  <p className="mt-2"><strong className="text-slate-900">Postal:</strong> CARTIEAI LLC · Texas, United States</p>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  EU/UK residents may also contact their local data-protection authority. We are committed to resolving concerns directly first.
                </p>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 text-slate-600 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-6 h-6" />
            <span className="text-sm">
              <span className="text-white">CARTIE<span className="text-emerald-700">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
            </span>
          </div>
          <p className="text-sm" data-testid="privacy-footer-entity">© 2026 CARTIEAI LLC · Texas, USA · All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
